import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'

/** Valore ritardato: evita una richiesta per ogni tasto premuto nei campi di ricerca. */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}

/**
 * Media query reattiva.
 *
 * Serve dove la differenza mobile/desktop non è solo di stile ma di **struttura** —
 * una tabella che diventa un elenco di card, un dialog che diventa un pannello dal
 * basso. Dove basta il CSS, si usano le classi responsive e non questo hook.
 *
 * Implementata con `useSyncExternalStore` e non con `useState` + `useEffect`: la
 * media query *è* uno store esterno, e leggerla nel render evita il primo frame col
 * valore sbagliato che l'accoppiata state/effect produce.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const list = window.matchMedia(query)
      list.addEventListener('change', onStoreChange)
      return () => list.removeEventListener('change', onStoreChange)
    },
    [query],
  )

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query])

  return useSyncExternalStore(subscribe, getSnapshot)
}

/** `true` da `lg` in su: la soglia a cui il layout passa a sidebar fissa. */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)')
}

/** Copia negli appunti, con esito. Usata per id e requestId nelle segnalazioni. */
export function useCopyToClipboard(): [boolean, (text: string) => Promise<void>] {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 1600)
    return () => clearTimeout(timer)
  }, [copied])

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return [copied, copy]
}
